import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { UseOutletContextOrder } from './Order';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { OrderDA } from '../../../../services/admin/order.service';
import { Error } from '../../../../models/error.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { getError } from '../../../../languages/translations/response';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { dateFormat, datetimeFormat12h } from '../../../../scripts/date.time.format.script';
import { toastSuccess } from '../../../../utils/toast.util';

import AppPreloader from '../../../../components/Preloader';
import AppPurchaseViewDraft from '../../purchase/component/PurchaseViewDraft';

export interface DeliveryNoteViewPageProps {};

let errorResponse: Error;

const DeliveryNoteViewPage: React.FunctionComponent<DeliveryNoteViewPageProps> = props => {
  const {setRoute, companyForUser} = UseOutletContextOrder()
  const {lang} = StateLanguage()
  const param = useParams()

  const [mounted, setMounted] = useState(false)
  const [deliveryNote, setDeliveryNote] = useState<any>(null)

  const loadDeliveryNote = async (id_company: string, id_order: string, id_delivery_note: string) => {
    await OrderDA.getDeliveryNote(id_company, id_order, id_delivery_note).then((response) => {
      if (response.status === 200) {
        setDeliveryNote(response.data?.data ?? response.data)
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch((error) => {
      errorWebHandler(lang, error)
    })
  }

  const submitSendDeliveryNote = async () => {
    if (!companyForUser || !deliveryNote) return
    const idOrder = param.id_order ?? deliveryNote.id_order
    if (!idOrder) return

    await OrderDA.sendDeliveryNote(companyForUser.id, idOrder, deliveryNote.id).then((response) => {
      if (response.status === 200) {
        toastSuccess(lang.labels.actionCompletedReturningToPage)
        loadDeliveryNote(companyForUser.id, idOrder, deliveryNote.id)
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch((error) => {
      errorWebHandler(lang, error)
    })
  }

  useEffect(() => {
    if (companyForUser && param.id_order && param.id_delivery_note) {
      loadDeliveryNote(companyForUser.id, param.id_order, param.id_delivery_note)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, param])

  useEffect(() => {
    setMounted(true)
    setRoute({path: {root: lang.labels.orders, branch: lang.labels.deliveryNote}, company: false})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  const idOrder = param.id_order ?? deliveryNote?.id_order

  return (
    <div className="w-100 h-100">
      { companyForUser && deliveryNote
        ?
        <div className="d-flex flex-column flex-lg-row">
          <div className="flex-lg-row-fluid order-2 order-lg-1 mb-5 mb-lg-0 me-lg-5">
          <div className="card card-flush mb-5">
            <div className="card-header">
              <div className="card-title">
                <h3 className="fw-bold">{deliveryNote.delivery_note_number}</h3>
              </div>
              <div className="card-toolbar">
                <div className="d-flex justify-content-end align-items-center">
                  { deliveryNote.status !== 'sent' &&
                    <button className="btn btn-primary me-2" type="button" onClick={submitSendDeliveryNote}>
                      {lang.labels.send}
                    </button>
                  }
                  <Link to={`/main/sale/order/view/${idOrder}`} className="btn btn-light-primary">
                    {lang.labels.backToOrder}
                  </Link>
                </div>
              </div>
            </div>
            <div className="card-body pt-0">
              <div className="row mb-5">
                <div className="col-md-6">
                  <table className="table table-row-bordered gy-3">
                    <tbody>
                      <tr>
                        <td className="text-muted w-150px">{lang.labels.number}</td>
                        <td className="fw-bold">{deliveryNote.delivery_note_number}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">{lang.labels.deliveryDate}</td>
                        <td className="fw-bold">{deliveryNote.delivery_date ? dateFormat(lang.code, deliveryNote.delivery_date) : '-'}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">{lang.labels.status}</td>
                        <td>
                          <span className={`badge badge-light-${deliveryNote.status === 'sent' ? 'success' : 'warning'} text-uppercase fs-7`}>
                            {deliveryNote.status === 'sent' ? lang.labels.sent : lang.labels.draft}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">{lang.labels.order}</td>
                        <td>
                          <Link to={`/main/sale/order/view/${idOrder}`} className="fw-bold text-primary">
                            {deliveryNote.order_number ?? '-'}
                          </Link>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="col-md-6">
                  <table className="table table-row-bordered gy-3">
                    <tbody>
                      { deliveryNote.storage_center &&
                        <tr>
                          <td className="text-muted w-150px">{lang.labels.storageCenter}</td>
                          <td className="fw-bold">{deliveryNote.storage_center.name ?? deliveryNote.storage_center_name ?? '-'}</td>
                        </tr>
                      }
                      { deliveryNote.delivery_address &&
                        <tr>
                          <td className="text-muted">{lang.labels.deliveryAddressLabel}</td>
                          <td className="fw-bold">{deliveryNote.delivery_address?.label ?? '-'}</td>
                        </tr>
                      }
                      { deliveryNote.sent_at &&
                        <tr>
                          <td className="text-muted">{lang.labels.sentAt}</td>
                          <td className="fw-bold">{datetimeFormat12h(lang.code, deliveryNote.sent_at)}</td>
                        </tr>
                      }
                      { deliveryNote.sent_method &&
                        <tr>
                          <td className="text-muted">{lang.labels.sentMethod}</td>
                          <td className="fw-bold text-capitalize">{deliveryNote.sent_method}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div className="card card-flush">
            <div className="card-header">
              <div className="card-title">
                <h3 className="fw-bold">{lang.labels.linesDelivered}</h3>
              </div>
            </div>
            <div className="card-body pt-0">
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed mb-2">
                  <thead className="border-bottom">
                    <tr className="text-muted text-uppercase fw-bolder fs-7">
                      <th className="min-w-250px">{lang.labels.detail}</th>
                      <th className="w-125px text-center">{lang.labels.unit}</th>
                      <th className="w-125px text-center">{lang.labels.quantityDelivered}</th>
                    </tr>
                  </thead>
                  { (deliveryNote.contents ?? deliveryNote.lines ?? []).length > 0
                    ?
                    <tbody className="text-secondary fw-bold">
                      { (deliveryNote.contents ?? deliveryNote.lines ?? []).map((item: any, index: number) => (
                        <tr key={index} className="row-dashed">
                          <td>{item.detail ?? '-'}</td>
                          <td className="text-center">{item.unit?.name ?? item.unit ?? '-'}</td>
                          <td className="text-center">{item.quantity_delivered ?? item.quantity ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                    :
                    <tbody>
                      <tr className="text-secondary fw-bold">
                        <td colSpan={3} className="text-center">{lang.labels.noRecordsFound}</td>
                      </tr>
                    </tbody>
                  }
                </table>
              </div>
            </div>
          </div>
          </div>
          <AppPurchaseViewDraft record={deliveryNote} showGeneratedPdf={true} companyId={companyForUser?.id} pdfPath={`orders/${idOrder}/delivery-notes`} documentId={deliveryNote?.id}></AppPurchaseViewDraft>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
};

export default DeliveryNoteViewPage;
